import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Upload, AlertCircle, CheckCircle2, XCircle, Loader2, Mail, Pause, Play, X, Search, Download, Database, RefreshCw, Trash2, FileAudio, Brain } from "lucide-react";
import ResponseRecovery from "@/components/admin/ResponseRecovery";
import TranscriptionRecovery from "@/components/admin/TranscriptionRecovery";
import EvaluationRecovery from "@/components/admin/EvaluationRecovery";
import Papa from "papaparse";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ALLOWED_EMAIL = "utkarshgupta@testera.ai";

interface CSVUser {
  full_name: string;
  email: string;
  password: string;
}

interface ImportResult {
  email: string;
  success: boolean;
  error?: string;
}

interface CSVInvitation {
  email: string;
  test_id: string;
}

interface InvitationResult {
  testId: string;
  email: string;
  success: boolean;
  error?: string;
}

interface BatchJob<T, R> {
  id: string;
  type: 'user-import' | 'invitations';
  totalItems: number;
  processedCount: number;
  batchSize: number;
  status: 'idle' | 'running' | 'paused' | 'completed' | 'error';
  items: T[];
  results: R[];
  startedAt: string;
  pausedAt?: string;
  completedAt?: string;
  errorMessage?: string;
}

interface VerificationResult {
  email: string;
  hasPortalAccess: boolean;
  hasTestInvitation: boolean;
  category: 'success' | 'no-portal' | 'no-invite' | 'no-user';
}

interface DiagnosisResult {
  test_response_id: string;
  test_id: string;
  test_title: string;
  candidate_email: string;
  candidate_name: string;
  completed_at: string;
  expected_questions: number;
  saved_responses: number;
  missing: number;
  video_files: number;
  audio_files: number;
  monitoring_question_coverage: number;
  recoverable: boolean;
}

interface DeleteInvitationResult {
  email: string;
  deletedCount: number;
  success: boolean;
  error?: string;
}

interface ParsedQuestion {
  question_type: string;
  content: {
    question: string;
    options?: string[];
    correctOption?: number;
    responseType?: string;
  };
  time_limit: number;
  marks: number;
  skill: string | null;
  difficulty: string | null;
  markscheme: {
    criteria?: { name: string; description: string; max_points: number }[];
    total_points?: number;
    overall_guidance?: string;
    suggested_answer?: string;
  } | null;
}

interface ParsedTest {
  test: {
    title: string;
    description: string;
    location: string;
    language: string;
    key_skills: string[];
    job_title?: string;
    job_description?: string;
    key_responsibilities?: string;
    years_of_experience?: string;
    work_arrangement?: string;
    work_presence?: string;
    industry?: string;
    seniority?: string;
    salary_range_min?: number;
    salary_range_max?: number;
    salary_currency?: string;
    publish_to_job_board?: boolean;
    webcam_monitoring_enabled?: boolean;
    screen_monitoring_enabled?: boolean;
    invitation_only?: boolean;
    status?: string;
    organization_id?: string;
    time_limit_type?: string;
    welcome_message?: string;
    completion_message?: string;
  };
  questions: ParsedQuestion[];
}

interface TestUploadResult {
  title: string;
  testId?: string;
  questionCount: number;
  success: boolean;
  error?: string;
}

export default function BulkUserImport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // User import state
  const [users, setUsers] = useState<CSVUser[]>([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Invitation state
  const [invitations, setInvitations] = useState<CSVInvitation[]>([]);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [invitationResults, setInvitationResults] = useState<InvitationResult[] | null>(null);
  const [dragActiveInvite, setDragActiveInvite] = useState(false);

  // Batch job state
  const [invitationJob, setInvitationJob] = useState<BatchJob<CSVInvitation, InvitationResult> | null>(null);
  const [userImportJob, setUserImportJob] = useState<BatchJob<CSVUser, ImportResult> | null>(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [pendingJob, setPendingJob] = useState<BatchJob<any, any> | null>(null);

  // Verification state
  const [verifyEmails, setVerifyEmails] = useState<string[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyResults, setVerifyResults] = useState<VerificationResult[] | null>(null);
  const [dragActiveVerify, setDragActiveVerify] = useState(false);

  // Diagnosis state
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [diagnosisResults, setDiagnosisResults] = useState<DiagnosisResult[]>([]);
  const [diagnosisDateFilter, setDiagnosisDateFilter] = useState<string>('2025-11-30');
  const [storageScanProgress, setStorageScanProgress] = useState<{ current: number; total: number } | null>(null);

  // Delete invitations state
  const [deleteEmails, setDeleteEmails] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState<DeleteInvitationResult[] | null>(null);
  const [dragActiveDelete, setDragActiveDelete] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

  // Bulk test upload state
  const [parsedTests, setParsedTests] = useState<ParsedTest[]>([]);
  const [uploadingTests, setUploadingTests] = useState(false);
  const [testUploadResults, setTestUploadResults] = useState<TestUploadResult[] | null>(null);
  const [dragActiveTest, setDragActiveTest] = useState(false);
  const [showTestConfirmDialog, setShowTestConfirmDialog] = useState(false);

  // Check authorization
  if (user?.email !== ALLOWED_EMAIL) {
    return (
      <div className="container max-w-4xl mx-auto py-12">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. This page is restricted to authorized administrators only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // LocalStorage helpers
  const saveJobToStorage = (job: BatchJob<any, any>, storageKey: string) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(job));
    } catch (error) {
      console.error('Failed to save job to localStorage:', error);
    }
  };

  const loadJobFromStorage = (storageKey: string): BatchJob<any, any> | null => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Failed to load job from localStorage:', error);
      return null;
    }
  };

  const clearJobFromStorage = (storageKey: string) => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear job from localStorage:', error);
    }
  };

  // Check for incomplete jobs on mount
  useEffect(() => {
    const savedInvitationJob = loadJobFromStorage('bulk-job-invitations');
    const savedUserImportJob = loadJobFromStorage('bulk-job-users');

    if (savedInvitationJob && (savedInvitationJob.status === 'running' || savedInvitationJob.status === 'paused')) {
      setPendingJob(savedInvitationJob);
      setShowResumeDialog(true);
    } else if (savedUserImportJob && (savedUserImportJob.status === 'running' || savedUserImportJob.status === 'paused')) {
      setPendingJob(savedUserImportJob);
      setShowResumeDialog(true);
    }
  }, []);

  const handleResumeJob = () => {
    if (!pendingJob) return;

    if (pendingJob.type === 'invitations') {
      setInvitationJob(pendingJob);
      setInvitations(pendingJob.items);
      setTimeout(() => runInvitationBatchJob(pendingJob), 100);
    } else if (pendingJob.type === 'user-import') {
      setUserImportJob(pendingJob);
      setUsers(pendingJob.items);
      setTimeout(() => runUserImportBatchJob(pendingJob), 100);
    }

    setShowResumeDialog(false);
    setPendingJob(null);
  };

  const handleDiscardJob = () => {
    if (pendingJob) {
      clearJobFromStorage(pendingJob.type === 'invitations' ? 'bulk-job-invitations' : 'bulk-job-users');
    }
    setShowResumeDialog(false);
    setPendingJob(null);
  };

  // Batch processing for invitations
  const processBatchInvitations = async (
    batch: CSVInvitation[],
    userId: string
  ): Promise<InvitationResult[]> => {
    const INNOVENTURE_ORG_ID = "dd2b47e7-9181-412d-9b3e-3ed1ce6c7edb";

    // 1. Batch insert all test invitations at once
    const invitationInserts = batch.map(inv => ({
      test_id: inv.test_id,
      email: inv.email.toLowerCase().trim(),
      invited_by: userId,
      status: "pending",
    }));

    const { error: inviteError } = await supabase
      .from("test_invitations")
      .upsert(invitationInserts, { 
        onConflict: 'test_id,email',
        ignoreDuplicates: true 
      });

    if (inviteError) {
      console.error('Batch invitation insert error:', inviteError);
      // If batch insert fails completely, return all as failed
      return batch.map(inv => ({
        testId: inv.test_id,
        email: inv.email,
        success: false,
        error: inviteError.message,
      }));
    }

    // 2. Batch fetch all user profiles at once (case-insensitive)
    const emails = batch.map(inv => inv.email.toLowerCase().trim());
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, email")
      .in("email", emails);

    // 3. Batch insert organization memberships for existing users
    if (profiles && profiles.length > 0) {
      const orgInserts = profiles.map(p => ({
        user_id: p.id,
        organization_id: INNOVENTURE_ORG_ID,
        added_by: userId,
        status: "active",
      }));

      const { error: orgError } = await supabase
        .from("user_organizations")
        .upsert(orgInserts, { 
          onConflict: 'user_id,organization_id',
          ignoreDuplicates: true 
        });

      if (orgError) {
        console.warn('Batch organization insert error:', orgError);
      }
    }

    // Return all as successful (invitations were inserted)
    return batch.map(inv => ({
      testId: inv.test_id,
      email: inv.email,
      success: true,
    }));
  };

  const runInvitationBatchJob = async (job: BatchJob<CSVInvitation, InvitationResult>) => {
    if (!user) return;

    const BATCH_SIZE = 500; // Increased from 100 - batch operations are efficient
    const DELAY_MS = 200;   // Reduced from 500ms
    let currentJob: BatchJob<CSVInvitation, InvitationResult> = { ...job, status: 'running' };
    setInvitationJob(currentJob);

    try {
      while (currentJob.processedCount < currentJob.totalItems && currentJob.status === 'running') {
        const startIdx = currentJob.processedCount;
        const batch = currentJob.items.slice(startIdx, startIdx + BATCH_SIZE);

        const batchResults = await processBatchInvitations(batch, user.id);

        currentJob = {
          ...currentJob,
          processedCount: currentJob.processedCount + batch.length,
          results: [...currentJob.results, ...batchResults],
        };

        setInvitationJob(currentJob);
        saveJobToStorage(currentJob, 'bulk-job-invitations');

        if (currentJob.processedCount < currentJob.totalItems) {
          await new Promise(resolve => setTimeout(resolve, DELAY_MS));
        }

        // Check if paused
        const currentState = invitationJob;
        if (currentState?.status === 'paused') {
          break;
        }
      }

      if (currentJob.processedCount >= currentJob.totalItems) {
        currentJob = {
          ...currentJob,
          status: 'completed',
          completedAt: new Date().toISOString(),
        };
        setInvitationJob(currentJob);
        setInvitationResults(currentJob.results);
        setSendingInvitations(false);
        clearJobFromStorage('bulk-job-invitations');

        const successCount = currentJob.results.filter(r => r.success).length;
        const failCount = currentJob.results.filter(r => !r.success).length;

        toast({
          title: "Invitations complete",
          description: `${successCount} successful, ${failCount} failed`,
        });
      }
    } catch (error) {
      currentJob = {
        ...currentJob,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      };
      setInvitationJob(currentJob);
      setSendingInvitations(false);
      saveJobToStorage(currentJob, 'bulk-job-invitations');

      toast({
        title: "Batch job failed",
        description: currentJob.errorMessage,
        variant: "destructive",
      });
    }
  };

  // Batch processing for user imports
  const processBatchUsers = async (batch: CSVUser[]): Promise<ImportResult[]> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('Session expired');
      }

      const { data, error } = await supabase.functions.invoke('bulk-import-users', {
        body: { users: batch },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      });

      if (error) throw error;
      return data.results || [];
    } catch (error: any) {
      return batch.map(user => ({
        email: user.email,
        success: false,
        error: error.message || 'Unknown error',
      }));
    }
  };

  const runUserImportBatchJob = async (job: BatchJob<CSVUser, ImportResult>) => {
    const BATCH_SIZE = 300;
    let currentJob: BatchJob<CSVUser, ImportResult> = { ...job, status: 'running' };
    setUserImportJob(currentJob);

    try {
      while (currentJob.processedCount < currentJob.totalItems && currentJob.status === 'running') {
        const startIdx = currentJob.processedCount;
        const batch = currentJob.items.slice(startIdx, startIdx + BATCH_SIZE);

        const batchResults = await processBatchUsers(batch);

        currentJob = {
          ...currentJob,
          processedCount: currentJob.processedCount + batch.length,
          results: [...currentJob.results, ...batchResults],
        };

        setUserImportJob(currentJob);
        saveJobToStorage(currentJob, 'bulk-job-users');

        // Check if paused
        const currentState = userImportJob;
        if (currentState?.status === 'paused') {
          break;
        }
      }

      if (currentJob.processedCount >= currentJob.totalItems) {
        currentJob = {
          ...currentJob,
          status: 'completed',
          completedAt: new Date().toISOString(),
        };
        setUserImportJob(currentJob);
        setResults(currentJob.results);
        setImporting(false);
        clearJobFromStorage('bulk-job-users');

        const successCount = currentJob.results.filter(r => r.success).length;
        const failCount = currentJob.results.filter(r => !r.success).length;

        toast({
          title: "Import complete",
          description: `${successCount} users imported, ${failCount} failed`,
        });
      }
    } catch (error) {
      currentJob = {
        ...currentJob,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      };
      setUserImportJob(currentJob);
      setImporting(false);
      saveJobToStorage(currentJob, 'bulk-job-users');

      toast({
        title: "Batch job failed",
        description: currentJob.errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedUsers = results.data as CSVUser[];
        
        // Validate required fields
        const validUsers = parsedUsers.filter(u => u.full_name && u.email && u.password);
        
        if (validUsers.length === 0) {
          toast({
            title: "Invalid CSV",
            description: "CSV must have columns: full_name, email, password",
            variant: "destructive",
          });
          return;
        }

        setUsers(validUsers);
        setResults(null);
        toast({
          title: "CSV loaded",
          description: `${validUsers.length} users ready to import`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const handleImport = async () => {
    if (users.length === 0) return;

    const newJob: BatchJob<CSVUser, ImportResult> = {
      id: `user-import-${Date.now()}`,
      type: 'user-import',
      totalItems: users.length,
      processedCount: 0,
      batchSize: 300,
      status: 'idle',
      items: users,
      results: [],
      startedAt: new Date().toISOString(),
    };

    setUserImportJob(newJob);
    setImporting(true);
    setResults(null);

    runUserImportBatchJob(newJob);
  };

  const downloadResults = () => {
    if (!results) return;
    
    const csv = Papa.unparse(results);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-results-${new Date().toISOString()}.csv`;
    a.click();
  };

  // Invitation handlers
  const handleDragInvite = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveInvite(true);
    } else if (e.type === "dragleave") {
      setDragActiveInvite(false);
    }
  }, []);

  const handleDropInvite = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveInvite(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleInvitationFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleInvitationFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleInvitationFile(e.target.files[0]);
    }
  };

  const handleInvitationFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedInvitations = results.data as any[];
        
        // Map columns (handles "Test Link" or "test_id")
        const validInvitations = parsedInvitations
          .filter(row => row.Email && (row['Test Link'] || row.test_id))
          .map(row => ({
            email: row.Email.trim().toLowerCase(),
            test_id: (row['Test Link'] || row.test_id).trim(),
          }));
        
        if (validInvitations.length === 0) {
          toast({
            title: "Invalid CSV",
            description: "CSV must have columns: Email, Test Link",
            variant: "destructive",
          });
          return;
        }

        setInvitations(validInvitations);
        setInvitationResults(null);
        toast({
          title: "CSV loaded",
          description: `${validInvitations.length} invitations ready to send`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const handleSendInvitations = async () => {
    if (invitations.length === 0 || !user) return;

    // Session validation
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData?.session) {
      toast({
        title: "Session expired",
        description: "Please refresh the page and log in again.",
        variant: "destructive",
      });
      return;
    }

    // Check superadmin status
    const { data: superadminCheck } = await supabase
      .rpc('is_superadmin', { user_uuid: sessionData.session.user.id });

    if (!superadminCheck) {
      toast({
        title: "Insufficient permissions",
        description: "You must be a superadmin to send bulk invitations.",
        variant: "destructive",
      });
      return;
    }

    const newJob: BatchJob<CSVInvitation, InvitationResult> = {
      id: `invitations-${Date.now()}`,
      type: 'invitations',
      totalItems: invitations.length,
      processedCount: 0,
      batchSize: 100,
      status: 'idle',
      items: invitations,
      results: [],
      startedAt: new Date().toISOString(),
    };

    setInvitationJob(newJob);
    setSendingInvitations(true);
    setInvitationResults(null);

    runInvitationBatchJob(newJob);
  };

  const downloadInvitationResults = () => {
    if (!invitationResults) return;
    
    const csv = Papa.unparse(invitationResults);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invitation-results-${new Date().toISOString()}.csv`;
    a.click();
  };

  // Group invitations by test
  const groupedInvitations = invitations.reduce((acc, inv) => {
    if (!acc[inv.test_id]) {
      acc[inv.test_id] = [];
    }
    acc[inv.test_id].push(inv.email);
    return acc;
  }, {} as Record<string, string[]>);

  // Verification handlers
  const handleDragVerify = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveVerify(true);
    } else if (e.type === "dragleave") {
      setDragActiveVerify(false);
    }
  }, []);

  const handleDropVerify = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveVerify(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleVerificationFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleVerificationFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleVerificationFile(e.target.files[0]);
    }
  };

  const handleVerificationFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedData = results.data as any[];
        
        // Extract emails (handles "email" or "Email" column)
        const emails = parsedData
          .filter(row => row.email || row.Email)
          .map(row => (row.email || row.Email).trim().toLowerCase());
        
        if (emails.length === 0) {
          toast({
            title: "Invalid CSV",
            description: "CSV must have an 'email' column",
            variant: "destructive",
          });
          return;
        }

        setVerifyEmails(emails);
        setVerifyResults(null);
        toast({
          title: "CSV loaded",
          description: `${emails.length} emails ready to verify`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  // Helper function to fetch all rows using pagination
  const fetchAllRows = async <T,>(
    queryFn: (offset: number, limit: number) => Promise<{ data: T[] | null; error: any }>
  ): Promise<T[]> => {
    const allData: T[] = [];
    const batchSize = 1000;
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await queryFn(offset, batchSize);
      if (error) throw error;
      
      if (data && data.length > 0) {
        allData.push(...data);
        offset += batchSize;
        hasMore = data.length === batchSize; // If we got a full batch, there might be more
      } else {
        hasMore = false;
      }
    }
    
    return allData;
  };

  const handleVerifyAccess = async () => {
    if (verifyEmails.length === 0) return;
    
    setVerifying(true);
    
    try {
      const INNOVENTURE_ORG_ID = "d4b5cd3c-483a-4574-9a20-ece44b857de6";
      
      // ========== DIAGNOSTIC: Check session state ==========
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('[Verify] Session state:', {
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        expiresAt: session?.expires_at,
        sessionError: sessionError?.message
      });
      
      if (!session) {
        throw new Error('No active session - please refresh the page and log in again');
      }
      // ====================================================
      
      // Query 1: Get ALL user_ids with portal access using pagination
      console.log('[Verify] Fetching user_organizations...');
      const orgData = await fetchAllRows(async (offset, limit) => {
        return supabase
          .from('user_organizations')
          .select('user_id')
          .eq('organization_id', INNOVENTURE_ORG_ID)
          .eq('status', 'active')
          .range(offset, offset + limit - 1);
      });
      
      console.log('[Verify] Query 1 - user_organizations:', {
        rowCount: orgData.length,
        firstThree: orgData.slice(0, 3)
      });
      
      // Query 2: Get ALL profiles using pagination
      console.log('[Verify] Fetching profiles...');
      const profileData = await fetchAllRows(async (offset, limit) => {
        return supabase
          .from('profiles')
          .select('id, email')
          .range(offset, offset + limit - 1);
      });
      
      console.log('[Verify] Query 2 - profiles:', {
        rowCount: profileData.length,
        firstThree: profileData.slice(0, 3)
      });
      
      // Query 3: Get ALL test invitations using pagination
      console.log('[Verify] Fetching test_invitations...');
      const invitationData = await fetchAllRows(async (offset, limit) => {
        return supabase
          .from('test_invitations')
          .select('email')
          .range(offset, offset + limit - 1);
      });
      
      console.log('[Verify] Query 3 - test_invitations:', {
        rowCount: invitationData.length,
        firstThree: invitationData.slice(0, 3)
      });
      
      // Query 4 (client-side): Build portal emails set by joining org user_ids with profiles
      const portalUserIds = new Set(orgData.map(d => d.user_id));
      const portalEmails = new Set<string>();
      
      profileData.forEach(profile => {
        if (portalUserIds.has(profile.id) && profile.email) {
          portalEmails.add(profile.email.toLowerCase());
        }
      });
      
      const invitedEmails = new Set(
        invitationData.map(d => d.email?.toLowerCase()).filter(Boolean)
      );
      
      // ========== DIAGNOSTIC: Log Set sizes ==========
      console.log('[Verify] Set sizes:', {
        portalUserIds: portalUserIds.size,
        portalEmails: portalEmails.size,
        invitedEmails: invitedEmails.size,
        samplePortalEmails: Array.from(portalEmails).slice(0, 5),
        sampleInvitedEmails: Array.from(invitedEmails).slice(0, 5),
        inputEmailsCount: verifyEmails.length,
        sampleInputEmails: verifyEmails.slice(0, 5)
      });
      // ===============================================
      
      // Categorize each email
      const results: VerificationResult[] = verifyEmails.map(email => {
        const hasPortal = portalEmails.has(email.toLowerCase());
        const hasInvite = invitedEmails.has(email.toLowerCase());
        
        let category: VerificationResult['category'];
        if (hasPortal && hasInvite) {
          category = 'success';
        } else if (!hasPortal && hasInvite) {
          category = 'no-portal';
        } else if (hasPortal && !hasInvite) {
          category = 'no-invite';
        } else {
          category = 'no-user';
        }
        
        return {
          email,
          hasPortalAccess: hasPortal,
          hasTestInvitation: hasInvite,
          category,
        };
      });
      
      setVerifyResults(results);
      toast({
        title: "Verification complete",
        description: `Checked ${results.length} emails`,
      });
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  const downloadCategoryEmails = (category: VerificationResult['category']) => {
    if (!verifyResults) return;
    
    const filtered = verifyResults.filter(r => r.category === category);
    const csv = Papa.unparse(filtered.map(r => ({ email: r.email })));
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${category}-emails-${new Date().toISOString()}.csv`;
    a.click();
  };

  // Delete invitations handlers
  const handleDragDelete = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveDelete(true);
    } else if (e.type === "dragleave") {
      setDragActiveDelete(false);
    }
  }, []);

  const handleDropDelete = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveDelete(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleDeleteFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleDeleteFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleDeleteFile(e.target.files[0]);
    }
  };

  const handleDeleteFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedData = results.data as any[];
        
        // Extract emails (handles "email" or "Email" column)
        const emails = parsedData
          .filter(row => row.email || row.Email)
          .map(row => (row.email || row.Email).trim().toLowerCase());
        
        if (emails.length === 0) {
          toast({
            title: "Invalid CSV",
            description: "CSV must have an 'email' column",
            variant: "destructive",
          });
          return;
        }

        setDeleteEmails(emails);
        setDeleteResults(null);
        toast({
          title: "CSV loaded",
          description: `${emails.length} emails ready for deletion`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const handleBulkDeleteInvitations = async () => {
    if (deleteEmails.length === 0) return;
    
    setShowDeleteConfirmDialog(false);
    setDeleting(true);
    setDeleteResults(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('Session expired');
      }

      const BATCH_SIZE = 100;
      const allResults: DeleteInvitationResult[] = [];

      for (let i = 0; i < deleteEmails.length; i += BATCH_SIZE) {
        const batch = deleteEmails.slice(i, i + BATCH_SIZE);
        
        const { data, error } = await supabase.functions.invoke('bulk-delete-invitations', {
          body: { emails: batch },
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`
          }
        });

        if (error) {
          console.error('[BulkDeleteInvitations] Batch error:', error);
          // Add failures for this batch
          batch.forEach(email => {
            allResults.push({
              email,
              deletedCount: 0,
              success: false,
              error: error.message,
            });
          });
        } else if (data?.results) {
          allResults.push(...data.results);
        }

        // Delay between batches
        if (i + BATCH_SIZE < deleteEmails.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setDeleteResults(allResults);
      
      const totalDeleted = allResults.reduce((sum, r) => sum + r.deletedCount, 0);
      const successCount = allResults.filter(r => r.success).length;
      const failCount = allResults.filter(r => !r.success).length;

      toast({
        title: "Deletion complete",
        description: `${totalDeleted} invitations deleted from ${successCount} emails. ${failCount} failed.`,
      });
    } catch (error: any) {
      toast({
        title: "Deletion failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const downloadDeleteResults = () => {
    if (!deleteResults) return;
    
    const csv = Papa.unparse(deleteResults);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `delete-invitations-results-${new Date().toISOString()}.csv`;
    a.click();
  };

  // Bulk test upload handlers
  const handleDragTest = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveTest(true);
    } else if (e.type === "dragleave") {
      setDragActiveTest(false);
    }
  }, []);

  const handleDropTest = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveTest(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleTestFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleTestFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleTestFile(e.target.files[0]);
    }
  };

  const parseMarkscheme = (criteriaStr: string, guidance: string, suggestedAnswer: string): ParsedQuestion['markscheme'] => {
    if (!criteriaStr && !guidance && !suggestedAnswer) return null;
    
    const criteria: { name: string; description: string; max_points: number }[] = [];
    let totalPoints = 0;
    
    if (criteriaStr) {
      // Format: "Name1:Description1:Points1|Name2:Description2:Points2"
      const parts = criteriaStr.split('|').filter(Boolean);
      for (const part of parts) {
        const [name, description, points] = part.split(':').map(s => s?.trim());
        if (name && description) {
          const maxPoints = parseInt(points) || 1;
          criteria.push({ name, description, max_points: maxPoints });
          totalPoints += maxPoints;
        }
      }
    }

    return {
      criteria: criteria.length > 0 ? criteria : undefined,
      total_points: totalPoints || undefined,
      overall_guidance: guidance || undefined,
      suggested_answer: suggestedAnswer || undefined,
    };
  };

  const parseQuestionsFromRow = (row: Record<string, string>): ParsedQuestion[] => {
    const questions: ParsedQuestion[] = [];
    
    // Support up to 20 questions per test
    for (let i = 1; i <= 20; i++) {
      const prefix = `q${i}_`;
      const questionText = row[`${prefix}question`]?.trim();
      
      if (!questionText) continue;
      
      const questionType = row[`${prefix}type`]?.trim() || 'essay';
      const options = row[`${prefix}options`]?.split('|').map(o => o.trim()).filter(Boolean) || [];
      const correctOption = parseInt(row[`${prefix}correct_option`]) || 0;
      const timeLimit = parseInt(row[`${prefix}time_limit`]) || 60;
      const marks = parseInt(row[`${prefix}marks`]) || 1;
      const skill = row[`${prefix}skill`]?.trim() || null;
      const difficulty = row[`${prefix}difficulty`]?.trim() || null;
      
      const markscheme = parseMarkscheme(
        row[`${prefix}markscheme_criteria`] || '',
        row[`${prefix}markscheme_guidance`] || '',
        row[`${prefix}markscheme_suggested_answer`] || ''
      );

      const content: ParsedQuestion['content'] = {
        question: questionText,
      };

      // Add options for MCQ
      if (questionType === 'multiple_choice' && options.length > 0) {
        content.options = options;
        content.correctOption = correctOption;
      }

      // Add response type for audio/video
      if (questionType === 'audio_response') {
        content.responseType = 'audio';
      } else if (questionType === 'video_response') {
        content.responseType = 'video';
      }

      questions.push({
        question_type: questionType,
        content,
        time_limit: timeLimit,
        marks,
        skill,
        difficulty,
        markscheme,
      });
    }

    return questions;
  };

  const handleTestFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data as Record<string, string>[];
        const tests: ParsedTest[] = [];

        for (const row of rows) {
          const title = row['title']?.trim();
          if (!title) continue;

          const questions = parseQuestionsFromRow(row);
          if (questions.length === 0) {
            console.warn(`[BulkTestUpload] No questions found for test: ${title}`);
          }

          const keySkills = row['key_skills']?.split('|').map(s => s.trim()).filter(Boolean) || ['General'];

          tests.push({
            test: {
              title,
              description: row['description']?.trim() || '',
              location: row['location']?.trim() || 'Remote',
              language: row['language']?.trim() || 'English',
              key_skills: keySkills,
              job_title: row['job_title']?.trim() || undefined,
              job_description: row['job_description']?.trim() || undefined,
              key_responsibilities: row['key_responsibilities']?.trim() || undefined,
              years_of_experience: row['years_of_experience']?.trim() || undefined,
              work_arrangement: row['work_arrangement']?.trim() || 'Full-Time',
              work_presence: row['work_presence']?.trim() || 'Fully On-site',
              industry: row['industry']?.trim() || undefined,
              seniority: row['seniority']?.trim() || undefined,
              salary_range_min: row['salary_range_min'] ? parseInt(row['salary_range_min']) : undefined,
              salary_range_max: row['salary_range_max'] ? parseInt(row['salary_range_max']) : undefined,
              salary_currency: row['salary_currency']?.trim() || 'INR',
              publish_to_job_board: row['publish_to_job_board']?.toLowerCase() === 'true',
              webcam_monitoring_enabled: row['webcam_monitoring_enabled']?.toLowerCase() !== 'false',
              screen_monitoring_enabled: row['screen_monitoring_enabled']?.toLowerCase() === 'true',
              invitation_only: row['invitation_only']?.toLowerCase() === 'true',
              status: row['status']?.trim() || 'draft',
              organization_id: row['organization_id']?.trim() || undefined,
              time_limit_type: row['time_limit_type']?.trim() || 'per_question',
              welcome_message: row['welcome_message']?.trim() || undefined,
              completion_message: row['completion_message']?.trim() || undefined,
            },
            questions,
          });
        }

        if (tests.length === 0) {
          toast({
            title: "Invalid CSV",
            description: "No valid tests found. Ensure CSV has 'title' column and at least one question per test.",
            variant: "destructive",
          });
          return;
        }

        setParsedTests(tests);
        setTestUploadResults(null);
        toast({
          title: "CSV loaded",
          description: `${tests.length} tests parsed with ${tests.reduce((sum, t) => sum + t.questions.length, 0)} total questions`,
        });
      },
      error: (error) => {
        toast({
          title: "Parse error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  const handleBulkTestUpload = async () => {
    if (parsedTests.length === 0) return;

    setShowTestConfirmDialog(false);
    setUploadingTests(true);
    setTestUploadResults(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('Session expired');
      }

      // Process in batches of 10 tests per request
      const BATCH_SIZE = 10;
      const allResults: TestUploadResult[] = [];

      for (let i = 0; i < parsedTests.length; i += BATCH_SIZE) {
        const batch = parsedTests.slice(i, i + BATCH_SIZE);

        const { data, error } = await supabase.functions.invoke('bulk-create-tests', {
          body: { tests: batch },
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`
          }
        });

        if (error) {
          console.error('[BulkTestUpload] Batch error:', error);
          batch.forEach(t => {
            allResults.push({
              title: t.test.title,
              questionCount: t.questions.length,
              success: false,
              error: error.message,
            });
          });
        } else if (data?.results) {
          allResults.push(...data.results);
        }

        // Delay between batches
        if (i + BATCH_SIZE < parsedTests.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setTestUploadResults(allResults);

      const successCount = allResults.filter(r => r.success).length;
      const failCount = allResults.filter(r => !r.success).length;

      toast({
        title: "Test creation complete",
        description: `${successCount} tests created, ${failCount} failed`,
      });
    } catch (error: any) {
      toast({
        title: "Test upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploadingTests(false);
    }
  };

  const downloadTestResults = () => {
    if (!testUploadResults) return;

    const csv = Papa.unparse(testUploadResults);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-tests-results-${new Date().toISOString()}.csv`;
    a.click();
  };

  const downloadTestTemplate = () => {
    const template = [
      // Header row
      [
        'title', 'description', 'location', 'language', 'key_skills', 'job_title', 'job_description',
        'key_responsibilities', 'years_of_experience', 'work_arrangement', 'work_presence', 'industry',
        'seniority', 'salary_range_min', 'salary_range_max', 'salary_currency', 'publish_to_job_board',
        'webcam_monitoring_enabled', 'screen_monitoring_enabled', 'invitation_only', 'status',
        'organization_id', 'time_limit_type', 'welcome_message', 'completion_message',
        // Question columns (up to 20 questions)
        'q1_type', 'q1_question', 'q1_options', 'q1_correct_option', 'q1_time_limit', 'q1_marks',
        'q1_skill', 'q1_difficulty', 'q1_markscheme_criteria', 'q1_markscheme_guidance', 'q1_markscheme_suggested_answer',
        'q2_type', 'q2_question', 'q2_options', 'q2_correct_option', 'q2_time_limit', 'q2_marks',
        'q2_skill', 'q2_difficulty', 'q2_markscheme_criteria', 'q2_markscheme_guidance', 'q2_markscheme_suggested_answer',
        'q3_type', 'q3_question', 'q3_options', 'q3_correct_option', 'q3_time_limit', 'q3_marks',
        'q3_skill', 'q3_difficulty', 'q3_markscheme_criteria', 'q3_markscheme_guidance', 'q3_markscheme_suggested_answer',
      ].join(','),
      // Example row 1 - MCQ test
      [
        'Sample MCQ Test', 'A test with multiple choice questions', 'Remote', 'English', 'JavaScript|React',
        'Frontend Developer', 'Looking for a skilled developer', 'Build UI components', '2-3 years',
        'Full-Time', 'Remote', 'Technology', 'Mid level', '50000', '100000', 'INR', 'false',
        'true', 'false', 'false', 'draft', '', 'per_question', 'Welcome to the test', 'Thank you for completing',
        // Q1
        'multiple_choice', 'What is React?', 'A library|A framework|A language|An OS', '0', '60', '1',
        'React', 'easy', 'Correct Answer:Identifies correct option:1', 'Check understanding of React basics', 'A library',
        // Q2
        'essay', 'Explain the virtual DOM', '', '', '120', '2',
        'React', 'medium', 'Explanation:Clear explanation of virtual DOM:2|Examples:Provides examples:1', 'Look for understanding of reconciliation', '',
        // Q3
        '', '', '', '', '', '', '', '', '', '', '',
      ].join(','),
      // Example row 2 - Video response test
      [
        'Sample Video Test', 'A test with video responses', 'New York', 'English', 'Communication|Presentation',
        'Sales Manager', 'Looking for sales talent', 'Client presentations', '5+ years',
        'Full-Time', 'Hybrid', 'Sales', 'Senior level', '80000', '120000', 'USD', 'true',
        'true', 'false', 'true', 'draft', '', 'per_question', 'Record your responses clearly', 'We will review your responses',
        // Q1
        'video_response', 'Introduce yourself and your sales experience', '', '', '120', '5',
        'Communication', 'medium', 'Clarity:Clear and confident speaking:2|Content:Relevant experience shared:2|Engagement:Engaging presentation:1', 'Assess communication skills', '',
        // Q2
        'video_response', 'How would you handle an objection?', '', '', '90', '5',
        'Sales', 'hard', 'Technique:Uses proper objection handling:3|Examples:Provides examples:2', 'Look for STAR method usage', '',
        // Q3
        '', '', '', '', '', '', '', '', '', '', '',
      ].join(','),
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-test-upload-template.csv';
    a.click();
  };

  // Diagnosis handlers
  const runDiagnosis = async () => {
    setDiagnosisLoading(true);
    setDiagnosisResults([]);
    setStorageScanProgress(null);

    try {
      console.log('[Diagnosis] Starting database query for date:', diagnosisDateFilter);
      
      // Step 1: Query test_responses with missing question_responses
      const startDate = new Date(diagnosisDateFilter);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(diagnosisDateFilter);
      endDate.setHours(23, 59, 59, 999);

      // Step 1: Fetch test_responses (no joins)
      const rawResults = await fetchAllRows<any>(async (offset, limit) => {
        return await supabase
          .from('test_responses')
          .select('id, test_id, candidate_id, completed_at')
          .eq('status', 'completed')
          .gte('completed_at', startDate.toISOString())
          .lte('completed_at', endDate.toISOString())
          .range(offset, offset + limit - 1);
      });

      console.log('[Diagnosis] Found', rawResults.length, 'completed test responses');

      // Step 1.5: Fetch test titles
      console.log('[Diagnosis] Fetching test titles...');
      const uniqueTestIds = [...new Set(rawResults.map(tr => tr.test_id))];
      const testTitlesMap = new Map<string, string>();

      for (let i = 0; i < uniqueTestIds.length; i += 500) {
        const batch = uniqueTestIds.slice(i, i + 500);
        const { data: tests } = await supabase
          .from('tests')
          .select('id, title')
          .in('id', batch);
        tests?.forEach(t => testTitlesMap.set(t.id, t.title));
      }

      // Step 1.6: Fetch profiles
      console.log('[Diagnosis] Fetching profiles...');
      const uniqueCandidateIds = [...new Set(rawResults.map(tr => tr.candidate_id))];
      const profilesMap = new Map<string, { email: string; full_name: string }>();

      for (let i = 0; i < uniqueCandidateIds.length; i += 500) {
        const batch = uniqueCandidateIds.slice(i, i + 500);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email, full_name')
          .in('id', batch);
        profiles?.forEach(p => profilesMap.set(p.id, { email: p.email, full_name: p.full_name }));
      }

      // Step 2: Batch count questions, saved responses, and monitoring events
      console.log('[Diagnosis] Fetching questions counts...');
      
      // Batch fetch all questions for these tests
      const { data: allQuestions } = await supabase
        .from('questions')
        .select('test_id')
        .in('test_id', uniqueTestIds);
      
      // Count questions per test_id
      const questionCountsByTestId = new Map<string, number>();
      allQuestions?.forEach(q => {
        questionCountsByTestId.set(q.test_id, (questionCountsByTestId.get(q.test_id) || 0) + 1);
      });

      console.log('[Diagnosis] Fetching saved responses counts...');
      
      // Batch fetch question_responses in chunks of 500
      const testResponseIds = rawResults.map(tr => tr.id);
      const savedResponsesByTestResponseId = new Map<string, number>();
      
      for (let i = 0; i < testResponseIds.length; i += 500) {
        const batch = testResponseIds.slice(i, i + 500);
        const { data: responses } = await supabase
          .from('question_responses')
          .select('test_response_id')
          .in('test_response_id', batch);
        
        responses?.forEach(r => {
          savedResponsesByTestResponseId.set(
            r.test_response_id, 
            (savedResponsesByTestResponseId.get(r.test_response_id) || 0) + 1
          );
        });
      }

      console.log('[Diagnosis] Building diagnosis results...');
      
      // Build diagnosis data using the batched counts (WITHOUT monitoring events first)
      const diagnosisData: DiagnosisResult[] = [];
      
      for (const tr of rawResults) {
        const expectedCount = questionCountsByTestId.get(tr.test_id) || 0;
        const savedCount = savedResponsesByTestResponseId.get(tr.id) || 0;
        const missing = expectedCount - savedCount;
        
        if (missing > 0) {
          const profile = profilesMap.get(tr.candidate_id);
          diagnosisData.push({
            test_response_id: tr.id,
            test_id: tr.test_id,
            test_title: testTitlesMap.get(tr.test_id) || 'Unknown Test',
            candidate_email: profile?.email || 'Unknown',
            candidate_name: profile?.full_name || 'Unknown',
            completed_at: tr.completed_at,
            expected_questions: expectedCount,
            saved_responses: savedCount,
            missing,
            video_files: 0, // Will be filled by storage scan
            audio_files: 0,
            monitoring_question_coverage: 0, // Will be filled below
            recoverable: false
          });
        }
      }

      console.log('[Diagnosis] Found', diagnosisData.length, 'responses with missing data');

      // NOW fetch monitoring events ONLY for filtered responses with missing data
      if (diagnosisData.length > 0) {
        console.log('[Diagnosis] Fetching monitoring events for filtered responses...');
        const responseIdsWithMissing = diagnosisData.map(d => d.test_response_id);
        const monitoringCoverageByTestResponseId = new Map<string, Set<string>>();
        
        // Batch in SMALLER chunks of 50 to avoid timeout on large table
        const batchSize = 50;
        const totalBatches = Math.ceil(responseIdsWithMissing.length / batchSize);
        
        for (let i = 0; i < responseIdsWithMissing.length; i += batchSize) {
          const batch = responseIdsWithMissing.slice(i, i + batchSize);
          const batchNum = Math.floor(i / batchSize) + 1;
          console.log(`[Diagnosis] Fetching monitoring events batch ${batchNum}/${totalBatches}`);
          
          const { data: events } = await supabase
            .from('test_monitoring_events')
            .select('test_response_id, question_id')
            .in('test_response_id', batch)
            .not('question_id', 'is', null);
          
          if (events) {
            events.forEach(e => {
              if (!monitoringCoverageByTestResponseId.has(e.test_response_id)) {
                monitoringCoverageByTestResponseId.set(e.test_response_id, new Set());
              }
              monitoringCoverageByTestResponseId.get(e.test_response_id)!.add(e.question_id!);
            });
          }
        }

        // Update monitoring_question_coverage in diagnosisData
        diagnosisData.forEach(d => {
          d.monitoring_question_coverage = monitoringCoverageByTestResponseId.get(d.test_response_id)?.size || 0;
        });
      }
      setDiagnosisResults(diagnosisData);

      // Step 3: Scan storage for file counts
      if (diagnosisData.length > 0) {
        const testResponseIds = diagnosisData.map(d => d.test_response_id);
        const batchSize = 50;
        let scannedCount = 0;

        for (let i = 0; i < testResponseIds.length; i += batchSize) {
          const batch = testResponseIds.slice(i, i + batchSize);
          setStorageScanProgress({ current: scannedCount, total: testResponseIds.length });

          console.log('[Diagnosis] Scanning storage batch', i / batchSize + 1, '/', Math.ceil(testResponseIds.length / batchSize));

          const { data: storageData, error } = await supabase.functions.invoke('diagnose-orphaned-responses', {
            body: { test_response_ids: batch }
          });

          if (error) {
            console.error('[Diagnosis] Storage scan error:', error);
          } else if (storageData?.results) {
            // Update diagnosis results with storage data
            setDiagnosisResults(prev => prev.map(item => {
              if (storageData.results[item.test_response_id]) {
                const storage = storageData.results[item.test_response_id];
                return {
                  ...item,
                  video_files: storage.video_count,
                  audio_files: storage.audio_count,
                  recoverable: storage.video_count >= item.missing && item.monitoring_question_coverage >= item.expected_questions
                };
              }
              return item;
            }));
          }

          scannedCount += batch.length;
        }

        setStorageScanProgress({ current: testResponseIds.length, total: testResponseIds.length });
      }

      toast({
        title: "Diagnosis Complete",
        description: `Found ${diagnosisData.length} test responses with missing data`,
      });
    } catch (error) {
      console.error('[Diagnosis] Error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (typeof error === 'object' && error !== null && 'message' in error)
          ? (error as any).message
          : String(error);
      toast({
        title: "Diagnosis Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setDiagnosisLoading(false);
      setStorageScanProgress(null);
    }
  };

  const downloadDiagnosisCSV = (recoverableOnly: boolean = false) => {
    const dataToExport = recoverableOnly 
      ? diagnosisResults.filter(r => r.recoverable)
      : diagnosisResults;

    const csv = Papa.unparse(dataToExport);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnosis-${recoverableOnly ? 'recoverable-' : ''}${new Date().toISOString()}.csv`;
    a.click();
  };

  return (
    <>
      {/* Resume Job Dialog */}
      <AlertDialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Incomplete Job Found</AlertDialogTitle>
            <AlertDialogDescription>
              You have an unfinished {pendingJob?.type === 'invitations' ? 'bulk invitation' : 'user import'} job:
              <div className="mt-2 space-y-1">
                <div>• {pendingJob?.processedCount} of {pendingJob?.totalItems} items processed</div>
                <div>• Started: {pendingJob?.startedAt ? new Date(pendingJob.startedAt).toLocaleString() : 'Unknown'}</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardJob}>Discard & Start Fresh</AlertDialogCancel>
            <AlertDialogAction onClick={handleResumeJob}>Resume Job</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    <div className="container max-w-6xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Bulk Operations</h1>
        <p className="text-muted-foreground">
          Import users or send test invitations in bulk
        </p>
      </div>

      <Tabs defaultValue="import" className="w-full">
        <TabsList className="grid w-full grid-cols-9 mb-6">
          <TabsTrigger value="import">Import Users</TabsTrigger>
          <TabsTrigger value="invitations">
            <Mail className="h-4 w-4 mr-2" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="bulk-tests">
            <Upload className="h-4 w-4 mr-2" />
            Bulk Tests
          </TabsTrigger>
          <TabsTrigger value="delete-invitations">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Inv
          </TabsTrigger>
          <TabsTrigger value="verify">
            <Search className="h-4 w-4 mr-2" />
            Verify
          </TabsTrigger>
          <TabsTrigger value="diagnosis">
            <Database className="h-4 w-4 mr-2" />
            Diagnosis
          </TabsTrigger>
          <TabsTrigger value="recovery">
            <RefreshCw className="h-4 w-4 mr-2" />
            Recovery
          </TabsTrigger>
          <TabsTrigger value="transcription">
            <FileAudio className="h-4 w-4 mr-2" />
            Transcription
          </TabsTrigger>
          <TabsTrigger value="evaluation">
            <Brain className="h-4 w-4 mr-2" />
            Evaluation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import">

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Upload CSV</CardTitle>
          <CardDescription>
            CSV must contain columns: full_name, email, password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-4 text-sm text-muted-foreground">
              Drag and drop your CSV file here, or click to browse
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button asChild>
                <span>Choose File</span>
              </Button>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* User Import Progress Card */}
      {userImportJob && userImportJob.status !== 'idle' && userImportJob.status !== 'completed' && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Processing User Import</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress 
              value={(userImportJob.processedCount / userImportJob.totalItems) * 100} 
              className="mb-4"
            />
            <div className="flex justify-between text-sm mb-4">
              <span>{userImportJob.processedCount} / {userImportJob.totalItems} processed</span>
              <span>
                ✓ {userImportJob.results.filter(r => r.success).length} | 
                ✗ {userImportJob.results.filter(r => !r.success).length}
              </span>
              <span>
                Batch {Math.ceil(userImportJob.processedCount / userImportJob.batchSize)} of {Math.ceil(userImportJob.totalItems / userImportJob.batchSize)}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (userImportJob.status === 'running') {
                    setUserImportJob({ ...userImportJob, status: 'paused', pausedAt: new Date().toISOString() });
                    saveJobToStorage({ ...userImportJob, status: 'paused' }, 'bulk-job-users');
                  } else if (userImportJob.status === 'paused') {
                    runUserImportBatchJob({ ...userImportJob, status: 'running' });
                  }
                }}
              >
                {userImportJob.status === 'running' ? (
                  <><Pause className="h-4 w-4 mr-2" />Pause</>
                ) : (
                  <><Play className="h-4 w-4 mr-2" />Resume</>
                )}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setUserImportJob(null);
                  clearJobFromStorage('bulk-job-users');
                  setImporting(false);
                  toast({ title: "Job cancelled" });
                }}
              >
                <X className="h-4 w-4 mr-2" />Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {users.length > 0 && !results && !userImportJob && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Preview ({users.length} users)</CardTitle>
            <CardDescription>Review users before importing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Password</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {user.password.substring(0, 8)}...
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <Button variant="outline" onClick={() => setUsers([])}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={importing || !!userImportJob}>
                {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {users.length} Users
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {results && (
        <Card>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
            <CardDescription>
              {results.filter(r => r.success).length} successful, {results.filter(r => !r.success).length} failed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-auto mb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((result, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {result.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell>{result.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {result.error || 'Success'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={downloadResults}>
                Download Results
              </Button>
              <Button onClick={() => {
                setResults(null);
                setUsers([]);
              }}>
                Import More Users
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="invitations">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Upload CSV</CardTitle>
              <CardDescription>
                CSV must contain columns: Email, Test Link (test ID or share token)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActiveInvite ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onDragEnter={handleDragInvite}
                onDragLeave={handleDragInvite}
                onDragOver={handleDragInvite}
                onDrop={handleDropInvite}
              >
                <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="mb-4 text-sm text-muted-foreground">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleInvitationFileInput}
                  className="hidden"
                  id="invitation-file-upload"
                />
                <label htmlFor="invitation-file-upload">
                  <Button asChild>
                    <span>Choose File</span>
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Invitation Progress Card */}
          {invitationJob && invitationJob.status !== 'idle' && invitationJob.status !== 'completed' && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Processing Invitations</CardTitle>
              </CardHeader>
              <CardContent>
                <Progress 
                  value={(invitationJob.processedCount / invitationJob.totalItems) * 100} 
                  className="mb-4"
                />
                <div className="flex justify-between text-sm mb-4">
                  <span>{invitationJob.processedCount} / {invitationJob.totalItems} processed</span>
                  <span>
                    ✓ {invitationJob.results.filter(r => r.success).length} | 
                    ✗ {invitationJob.results.filter(r => !r.success).length}
                  </span>
                  <span>
                    Batch {Math.ceil(invitationJob.processedCount / invitationJob.batchSize)} of {Math.ceil(invitationJob.totalItems / invitationJob.batchSize)}
                    {invitationJob.totalItems > invitationJob.processedCount && 
                      ` • ~${Math.ceil((invitationJob.totalItems - invitationJob.processedCount) / invitationJob.batchSize * 0.5)}s remaining`
                    }
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (invitationJob.status === 'running') {
                        setInvitationJob({ ...invitationJob, status: 'paused', pausedAt: new Date().toISOString() });
                        saveJobToStorage({ ...invitationJob, status: 'paused' }, 'bulk-job-invitations');
                      } else if (invitationJob.status === 'paused') {
                        runInvitationBatchJob({ ...invitationJob, status: 'running' });
                      }
                    }}
                  >
                    {invitationJob.status === 'running' ? (
                      <><Pause className="h-4 w-4 mr-2" />Pause</>
                    ) : (
                      <><Play className="h-4 w-4 mr-2" />Resume</>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setInvitationJob(null);
                      clearJobFromStorage('bulk-job-invitations');
                      setSendingInvitations(false);
                      toast({ title: "Job cancelled" });
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {invitations.length > 0 && !invitationResults && !invitationJob && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Preview ({invitations.length} invitations)</CardTitle>
                <CardDescription>Grouped by test - Review before sending</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-auto">
                  {Object.entries(groupedInvitations).map(([testId, emails]) => (
                    <div key={testId} className="mb-4 p-4 border rounded-lg">
                      <div className="font-semibold mb-2">
                        Test: {testId.substring(0, 8)}...
                        <span className="ml-2 text-sm text-muted-foreground">
                          ({emails.length} invitations)
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {emails.slice(0, 3).join(", ")}
                        {emails.length > 3 && `, +${emails.length - 3} more`}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <Button variant="outline" onClick={() => setInvitations([])}>
                    Cancel
                  </Button>
                  <Button onClick={handleSendInvitations} disabled={sendingInvitations || !!invitationJob}>
                    {sendingInvitations && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send {invitations.length} Invitations
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {invitationResults && (
            <Card>
              <CardHeader>
                <CardTitle>Invitation Results</CardTitle>
                <CardDescription>
                  {invitationResults.filter(r => r.success).length} successful, {invitationResults.filter(r => !r.success).length} failed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-auto mb-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Status</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="w-32">Test ID</TableHead>
                        <TableHead>Error Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invitationResults.map((result, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {result.success ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-destructive" />
                            )}
                          </TableCell>
                          <TableCell>{result.email}</TableCell>
                          <TableCell className="font-mono text-xs">
                            {result.testId.substring(0, 12)}...
                          </TableCell>
                          <TableCell className={`text-sm ${result.success ? 'text-muted-foreground' : 'text-destructive'}`}>
                            {result.error || 'Success'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex justify-end gap-4">
                  <Button variant="outline" onClick={downloadInvitationResults}>
                    Download Results
                  </Button>
                  <Button onClick={() => {
                    setInvitationResults(null);
                    setInvitations([]);
                  }}>
                    Send More Invitations
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="verify">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Upload CSV</CardTitle>
              <CardDescription>
                CSV must contain an 'email' column with emails to verify
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActiveVerify ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onDragEnter={handleDragVerify}
                onDragLeave={handleDragVerify}
                onDragOver={handleDragVerify}
                onDrop={handleDropVerify}
              >
                <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="mb-4 text-sm text-muted-foreground">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleVerificationFileInput}
                  className="hidden"
                  id="verification-file-upload"
                />
                <label htmlFor="verification-file-upload">
                  <Button asChild>
                    <span>Choose File</span>
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>

          {verifyEmails.length > 0 && !verifyResults && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Ready to Verify</CardTitle>
                <CardDescription>{verifyEmails.length} emails loaded</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end gap-4">
                  <Button variant="outline" onClick={() => setVerifyEmails([])}>
                    Cancel
                  </Button>
                  <Button onClick={handleVerifyAccess} disabled={verifying}>
                    {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Verify {verifyEmails.length} Emails
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {verifyResults && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Success Card */}
              <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      Success
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadCategoryEmails('success')}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {verifyResults.filter(r => r.category === 'success').length}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Both portal & test access
                  </p>
                </CardContent>
              </Card>

              {/* No Portal Card */}
              <Card className="border-yellow-200 bg-yellow-50/50 dark:border-yellow-900 dark:bg-yellow-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                      No Portal
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadCategoryEmails('no-portal')}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {verifyResults.filter(r => r.category === 'no-portal').length}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Test invite but no portal
                  </p>
                </CardContent>
              </Card>

              {/* No Invite Card */}
              <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-900 dark:bg-orange-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      No Invite
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadCategoryEmails('no-invite')}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {verifyResults.filter(r => r.category === 'no-invite').length}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Portal access but no test
                  </p>
                </CardContent>
              </Card>

              {/* No User Card */}
              <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      No User
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadCategoryEmails('no-user')}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {verifyResults.filter(r => r.category === 'no-user').length}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Neither portal nor test
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Diagnosis Tab */}
        <TabsContent value="diagnosis">
          <Card>
            <CardHeader>
              <CardTitle>Response Diagnosis</CardTitle>
              <CardDescription>
                Identify test responses with missing question_responses and check storage files for recovery
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Filter & Run Button */}
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Filter by Date</label>
                  <input
                    type="date"
                    value={diagnosisDateFilter}
                    onChange={(e) => setDiagnosisDateFilter(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <Button
                  onClick={runDiagnosis}
                  disabled={diagnosisLoading}
                  className="min-w-[200px]"
                >
                  {diagnosisLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Running Diagnosis...
                    </>
                  ) : (
                    <>
                      <Database className="mr-2 h-4 w-4" />
                      Run Diagnosis
                    </>
                  )}
                </Button>
              </div>

              {/* Storage Scan Progress */}
              {storageScanProgress && (
                <Alert>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertDescription>
                    Scanning storage files: {storageScanProgress.current} / {storageScanProgress.total}
                    <Progress 
                      value={(storageScanProgress.current / storageScanProgress.total) * 100} 
                      className="mt-2"
                    />
                  </AlertDescription>
                </Alert>
              )}

              {/* Summary Statistics */}
              {diagnosisResults.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Affected Responses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{diagnosisResults.length}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Missing Responses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {diagnosisResults.reduce((sum, r) => sum + r.missing, 0)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Recoverable</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {diagnosisResults.filter(r => r.recoverable).length}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Recovery Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {Math.round((diagnosisResults.filter(r => r.recoverable).length / diagnosisResults.length) * 100)}%
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Export Buttons */}
              {diagnosisResults.length > 0 && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => downloadDiagnosisCSV(false)}>
                    <Download className="mr-2 h-4 w-4" />
                    Export All ({diagnosisResults.length})
                  </Button>
                  <Button variant="outline" onClick={() => downloadDiagnosisCSV(true)}>
                    <Download className="mr-2 h-4 w-4" />
                    Export Recoverable Only ({diagnosisResults.filter(r => r.recoverable).length})
                  </Button>
                </div>
              )}

              {/* Results Table */}
              {diagnosisResults.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[600px] overflow-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Candidate</TableHead>
                          <TableHead>Test</TableHead>
                          <TableHead className="text-center">Expected</TableHead>
                          <TableHead className="text-center">Saved</TableHead>
                          <TableHead className="text-center">Missing</TableHead>
                          <TableHead className="text-center">Videos</TableHead>
                          <TableHead className="text-center">Audio</TableHead>
                          <TableHead className="text-center">Monitoring</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {diagnosisResults.map((result) => (
                          <TableRow key={result.test_response_id}>
                            <TableCell>
                              <div className="font-medium">{result.candidate_name}</div>
                              <div className="text-xs text-muted-foreground">{result.candidate_email}</div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{result.test_title}</div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(result.completed_at).toLocaleString()}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{result.expected_questions}</TableCell>
                            <TableCell className="text-center">{result.saved_responses}</TableCell>
                            <TableCell className="text-center">
                              <span className="font-semibold text-amber-600 dark:text-amber-400">
                                {result.missing}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">{result.video_files}</TableCell>
                            <TableCell className="text-center">{result.audio_files}</TableCell>
                            <TableCell className="text-center">{result.monitoring_question_coverage}</TableCell>
                            <TableCell className="text-center">
                              {result.recoverable ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mx-auto" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mx-auto" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {diagnosisResults.length === 0 && !diagnosisLoading && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Click "Run Diagnosis" to scan for test responses with missing question_responses.
                    This will query the database and check storage files to determine recoverability.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recovery Tab */}
        <TabsContent value="recovery">
          <ResponseRecovery />
        </TabsContent>

        {/* Bulk Tests Tab */}
        <TabsContent value="bulk-tests">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                Bulk Test Upload
              </CardTitle>
              <CardDescription>
                Upload a CSV with complete test definitions including questions and markschemes.
                Each row represents one test with up to 20 questions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Template Download */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="mb-2">
                    <strong>CSV Format:</strong> One row per test. Question columns use pattern: q1_type, q1_question, q1_options, etc.
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div><strong>Test fields:</strong> title, description, location, language, key_skills (pipe-separated), job_title, status, organization_id, etc.</div>
                    <div><strong>Question types:</strong> multiple_choice, essay, audio_response, video_response, coding</div>
                    <div><strong>MCQ options:</strong> Use pipe separator (Option1|Option2|Option3)</div>
                    <div><strong>Markscheme criteria:</strong> Name:Description:Points format, pipe-separated for multiple</div>
                  </div>
                  <Button variant="outline" size="sm" className="mt-3" onClick={downloadTestTemplate}>
                    <Download className="mr-2 h-4 w-4" />
                    Download Template CSV
                  </Button>
                </AlertDescription>
              </Alert>

              {/* Upload Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActiveTest ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onDragEnter={handleDragTest}
                onDragLeave={handleDragTest}
                onDragOver={handleDragTest}
                onDrop={handleDropTest}
              >
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="mb-4 text-sm text-muted-foreground">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleTestFileInput}
                  className="hidden"
                  id="test-file-upload"
                />
                <label htmlFor="test-file-upload">
                  <Button variant="outline" asChild>
                    <span>Choose File</span>
                  </Button>
                </label>
              </div>

              {/* Preview */}
              {parsedTests.length > 0 && (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      <strong>{parsedTests.length} tests loaded</strong> with {parsedTests.reduce((sum, t) => sum + t.questions.length, 0)} total questions.
                    </AlertDescription>
                  </Alert>

                  {/* Preview Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[300px] overflow-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead className="text-center">Questions</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Organization</TableHead>
                            <TableHead>Location</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parsedTests.map((test, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{test.test.title}</TableCell>
                              <TableCell className="text-center">
                                {test.questions.length === 0 ? (
                                  <span className="text-red-500">0 ⚠️</span>
                                ) : (
                                  test.questions.length
                                )}
                              </TableCell>
                              <TableCell>{test.test.status || 'draft'}</TableCell>
                              <TableCell className="font-mono text-xs">{test.test.organization_id || '-'}</TableCell>
                              <TableCell>{test.test.location}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Warnings */}
              {parsedTests.some(t => t.questions.length === 0) && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    Some tests have no questions. They will still be created but will be empty.
                  </AlertDescription>
                </Alert>
              )}

              {/* Upload Button */}
              {parsedTests.length > 0 && !uploadingTests && !testUploadResults && (
                <Button
                  onClick={() => setShowTestConfirmDialog(true)}
                  className="w-full"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Create {parsedTests.length} Tests
                </Button>
              )}

              {/* Progress */}
              {uploadingTests && (
                <Alert>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertDescription>
                    Creating tests... Please wait.
                  </AlertDescription>
                </Alert>
              )}

              {/* Results */}
              {testUploadResults && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Tests Created
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {testUploadResults.filter(r => r.success).length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          Failed
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-red-600">
                          {testUploadResults.filter(r => !r.success).length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Download Button */}
                  <Button variant="outline" onClick={downloadTestResults}>
                    <Download className="mr-2 h-4 w-4" />
                    Download Results
                  </Button>

                  {/* Results Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[400px] overflow-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>Test ID</TableHead>
                            <TableHead className="text-center">Questions</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                            <TableHead>Error</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {testUploadResults.map((result, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{result.title}</TableCell>
                              <TableCell className="font-mono text-xs">
                                {result.testId ? (
                                  <a 
                                    href={`/test-details/${result.testId}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline"
                                  >
                                    {result.testId.slice(0, 8)}...
                                  </a>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-center">{result.questionCount}</TableCell>
                              <TableCell className="text-center">
                                {result.success ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-red-600 mx-auto" />
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {result.error || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delete Invitations Tab */}
        <TabsContent value="delete-invitations">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Delete Test Invitations
              </CardTitle>
              <CardDescription>
                Upload a CSV with an 'email' column. All test invitations for these emails will be permanently deleted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Upload Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActiveDelete ? 'border-red-500 bg-red-500/5' : 'border-border'
                }`}
                onDragEnter={handleDragDelete}
                onDragLeave={handleDragDelete}
                onDragOver={handleDragDelete}
                onDrop={handleDropDelete}
              >
                <Trash2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="mb-4 text-sm text-muted-foreground">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleDeleteFileInput}
                  className="hidden"
                  id="delete-file-upload"
                />
                <label htmlFor="delete-file-upload">
                  <Button variant="outline" asChild>
                    <span>Choose File</span>
                  </Button>
                </label>
              </div>

              {/* Preview */}
              {deleteEmails.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{deleteEmails.length} emails loaded.</strong> All test invitations for these emails will be deleted.
                    <div className="mt-2 text-xs text-muted-foreground">
                      First 5: {deleteEmails.slice(0, 5).join(', ')}{deleteEmails.length > 5 ? '...' : ''}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Delete Button */}
              {deleteEmails.length > 0 && !deleting && !deleteResults && (
                <Button
                  onClick={() => setShowDeleteConfirmDialog(true)}
                  variant="destructive"
                  className="w-full"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete All Invitations for {deleteEmails.length} Emails
                </Button>
              )}

              {/* Progress */}
              {deleting && (
                <Alert>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertDescription>
                    Deleting invitations... Please wait.
                  </AlertDescription>
                </Alert>
              )}

              {/* Results */}
              {deleteResults && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Emails Processed
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {deleteResults.filter(r => r.success).length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Trash2 className="h-4 w-4 text-blue-600" />
                          Invitations Deleted
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-blue-600">
                          {deleteResults.reduce((sum, r) => sum + r.deletedCount, 0)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-600" />
                          Failed
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-red-600">
                          {deleteResults.filter(r => !r.success).length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Download Button */}
                  <Button variant="outline" onClick={downloadDeleteResults}>
                    <Download className="mr-2 h-4 w-4" />
                    Download Results
                  </Button>

                  {/* Results Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[400px] overflow-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead className="text-center">Deleted Count</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                            <TableHead>Error</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deleteResults.map((result, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono text-sm">{result.email}</TableCell>
                              <TableCell className="text-center">{result.deletedCount}</TableCell>
                              <TableCell className="text-center">
                                {result.success ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-red-600 mx-auto" />
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {result.error || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transcription Recovery Tab */}
        <TabsContent value="transcription">
          <TranscriptionRecovery />
        </TabsContent>

        {/* Evaluation Recovery Tab */}
        <TabsContent value="evaluation">
          <EvaluationRecovery />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete ALL test invitations for <strong>{deleteEmails.length} emails</strong>.
              <br /><br />
              This action cannot be undone. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeleteInvitations}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete All Invitations
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test Upload Confirmation Dialog */}
      <AlertDialog open={showTestConfirmDialog} onOpenChange={setShowTestConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Confirm Test Creation
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to create <strong>{parsedTests.length} tests</strong> with a total of <strong>{parsedTests.reduce((sum, t) => sum + t.questions.length, 0)} questions</strong>.
              <br /><br />
              Tests will be created with status "{parsedTests[0]?.test?.status || 'draft'}".
              {parsedTests.some(t => t.test.organization_id) && (
                <><br />Some tests will be assigned to an organization.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkTestUpload}>
              Create Tests
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </>
  );
}
